import React, { useEffect, useState } from "react";
import Leftbar from "../../components/Leftsidebar";
import Topbar from "../../components/Topbar";
import { Link } from "react-router-dom";
import { useParams } from 'react-router-dom';
import { UserdetailsInfo, usTimezone, formatReferralSource } from "../Utils/utils";
import Loaderspinner from "../../components/Loader";
import "./viewuser.css";



const USERTYPE_LABELS = {
  deal: "Lender",
  raiser: "Borrower",
};

const INVEST_TYPE_LABELS = {
  loan: "Private Money Loan",
  partner: "Private Money Partner",
};

const PROPERTY_TYPE_LABELS = {
  SingleFamily: "Single Family Residence",
  MultiFamily: "Multi Family Acquisition",
  CommercialProperty: "Commercial Property",
  Land: "Land",
  GroundUp: "Ground Up Development",
  MobileHome: "Mobile Home",
  Other: "Other",
};

const POSITION_LABELS = {
  first: "First Lien Position",
  second: "Second Lien Position",
  both: "Both",
};

const INDIVIDUAL_INVEST_LABELS = {
  '25000-50000': "Micro-Placements: $25,000 – $50,000",
  '50001-150000': "Strategic Placements: $50,001 – $150,000",
  '150001-500000': "Growth Placements: $150,001 – $500,000",
  '500000+': "Institutional/Syndication Scale: $500,000+",
};

const EXPERIENCE_LABELS = {
  any: "Any experience level",
  '3': "3 or more comparable deals",
  '6': "6 or more comparable deals",
  '10': "10 or more comparable deals",
};

const IDENFY_STATUS_CHIPS = {
  APPROVED: { label: "Verified", className: "vu-chip--green" },
  PENDING: { label: "Pending Review", className: "vu-chip--orange" },
  REJECTED: { label: "Rejected", className: "vu-chip--red" },
};

const SOCIAL_LINKS = [
  { key: 'website_url', label: "Website" },
  { key: 'linkedin_url', label: "LinkedIn" },
  { key: 'facebook_url', label: "Facebook" },
  { key: 'instagram_url', label: "Instagram" },
];

const NOT_AVAILABLE = "Not available";

const mapList = (values, labels) => {
  if (!Array.isArray(values) || values.length === 0) return NOT_AVAILABLE;
  return values.map((value) => labels[value] || value).join(', ');
};

const mapValue = (value, labels) => {
  if (!value) return NOT_AVAILABLE;
  return labels[value] || value;
};

const formatDate = (value) => {
  if (!value) return NOT_AVAILABLE;
  const formatted = usTimezone(value);
  return formatted === "Invalid date" ? NOT_AVAILABLE : formatted;
};

const formatCurrency = (value) => {
  if (!value) return NOT_AVAILABLE;
  const amount = Number(value);
  if (Number.isNaN(amount)) return NOT_AVAILABLE;
  return `$${amount.toLocaleString()}`;
};

const formatIndividualInvest = (value) => {
  if (!value) return NOT_AVAILABLE;
  if (value.startsWith('custom:')) {
    return formatCurrency(value.slice('custom:'.length)) + " (custom)";
  }
  return INDIVIDUAL_INVEST_LABELS[value] || value;
};

const formatInvestTime = (value) => {
  if (!value) return NOT_AVAILABLE;
  const months = Number(value);
  if (Number.isNaN(months)) return NOT_AVAILABLE;
  return `${months} month${months === 1 ? '' : 's'}`;
};

const formatAddress = (about) => {
  const line = [about.address, about.city].filter(Boolean).join(', ');
  const stateZip = [about.state, about.zip_code].filter(Boolean).join(' - ');
  const full = [line, stateZip].filter(Boolean).join(', ');
  return full || NOT_AVAILABLE;
};

const toHref = (url) => (/^https?:\/\//i.test(url) ? url : `https://${url}`);

const DetailTile = ({ label, value }) => (
  <div className="col-md-4 mb-3">
    <div className="bg-light p-3 rounded shadow-sm h-100">
      <h6 className="projectdetails-title">{label}</h6>
      <h6 className="text-muted project-descp">{value}</h6>
    </div>
  </div>
);

const IdenfyStatusTile = ({ status }) => {
  const chip = IDENFY_STATUS_CHIPS[status];
  return (
    <div className="col-md-4 mb-3">
      <div className="bg-light p-3 rounded shadow-sm h-100">
        <h6 className="projectdetails-title">Identity Verification</h6>
        <span className={`vu-chip ${chip ? chip.className : 'vu-chip--gray'}`}>
          {chip ? chip.label : "Not Verified"}
        </span>
      </div>
    </div>
  );
};

const SuspendedTile = ({ isSuspended }) => (
  <div className="col-md-4 mb-3">
    <div className="bg-light p-3 rounded shadow-sm h-100">
      <h6 className="projectdetails-title">Account Status</h6>
      <span className={`vu-chip ${isSuspended ? 'vu-chip--red' : 'vu-chip--green'}`}>
        {isSuspended ? "Suspended" : "Active"}
      </span>
    </div>
  </div>
);

// Weekly deals newsletter is opt-out: lenders get it until they unsubscribe
// from the email link or the app's Settings toggle.
const NewsletterTile = ({ about }) => {
  const known = typeof about.newsletter_opt_out === 'boolean';
  const optedOut = about.newsletter_opt_out === true;
  return (
    <div className="col-md-4 mb-3">
      <div className="bg-light p-3 rounded shadow-sm h-100">
        <h6 className="projectdetails-title">Weekly Newsletter</h6>
        {known ? (
          <>
            <span className={`vu-chip ${optedOut ? 'vu-chip--red' : 'vu-chip--green'}`}>
              {optedOut ? "Unsubscribed" : "Subscribed"}
            </span>
            {optedOut && about.newsletter_unsubscribed_at &&
              <h6 className="text-muted project-descp mt-2 mb-0">
                Since {formatDate(about.newsletter_unsubscribed_at)}
              </h6>
            }
          </>
        ) : (
          <h6 className="text-muted project-descp">{NOT_AVAILABLE}</h6>
        )}
      </div>
    </div>
  );
};

const SocialLinkTile = ({ label, url }) => (
  <div className="col-md-4 mb-3">
    <div className="bg-light p-3 rounded shadow-sm h-100">
      <h6 className="projectdetails-title">{label}</h6>
      <h6 className="project-descp">
        <a className="vu-link" href={toHref(url)} target="_blank" rel="noreferrer">{url}</a>
      </h6>
    </div>
  </div>
);

const InfoRow = ({ label, value }) => (
  <div className="row">
    <div className="col-3">
      <label style={{ fontSize: 14, marginRight: 20, marginLeft: 0 }}>
        {label} :
      </label>
    </div>
    <div className="col-8">
      <label style={{ fontSize: 14, marginRight: 20, marginLeft: 0 }}>
        {value}
      </label>
    </div>
  </div>
);

const ViewUser = () => {

  const { id } = useParams()

  const [showSidebar, setShowSidebar] = useState(false);
  const [userInfo, setUserInfo] = useState({});
  const [loading, setLoading] = useState(false)


  const handleSidebar = () => {
    setShowSidebar(!showSidebar);
  };

  const handleSidebarClose = () => {
    setShowSidebar(false);
  };

  const getUserviewdetails = async () => {
    try {
      setLoading(true);
      let resp = await UserdetailsInfo(id);
      setUserInfo(resp?.data)
    } catch (error) {
      console.log(error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (id) {
      getUserviewdetails()
    }
  }, [id])

  const about = userInfo?.about || {};
  const isLender = about.usertype === 'deal';

  const socialLinks = SOCIAL_LINKS
    .map((link) => ({ ...link, url: about[link.key] }))
    .filter((link) => link.url);

  return (
    <>
      {loading && <Loaderspinner />}
      <div class="g-sidenav-show  bg-gray-100">
        <div id="wrapper">
          <Leftbar showSidebar={showSidebar}
            handleSidebarClose={handleSidebarClose} />
          <main className="main-content border-radius-lg">
            <Topbar handleSidebar={handleSidebar} />

            <div className="header header-bg pb-6">
              <div className="container-fluid">
                <div className="header-body">
                  <div className="row align-items-center py-4">
                    <div className="col-lg-6 col-7">
                      <nav
                        aria-label="breadcrumb"
                        className="d-none d-md-inline-block ml-md-4"
                      >
                        <ol className="breadcrumb breadcrumb-links breadcrumb-dark" style={{ background: "transparent" }}>
                          <li className="breadcrumb-item">
                            <Link to="/dashboard">
                              <i className="fas fa-home" />
                            </Link>
                          </li>
                          <li className="breadcrumb-item mt-1">
                            <a className="" href="/view-user/101" style={{ fontSize: "15px" }}>View User </a>
                          </li>
                        </ol>
                      </nav>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="container-fluid mt--6">
              <div className="row">
                <div className="col">
                  <div className="card">
                    <div className="card-body">

                      <div className="col-md-12">
                        <div
                          style={{
                            width: 145,
                            height: 145,
                            borderRadius: 110,
                            border: about.photo ? "none" : "2px #87BFBB solid",
                            background: about.photo ? "transparent" : "#87BFBB",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                          }}
                        >
                          {about.photo ? (
                            <img
                              src={about.photo}
                              alt="userProfile"
                              style={{ width: "100%", height: "100%", borderRadius: "50%", objectFit: "cover" }}
                            />
                          ) : (
                            <span
                              className="text-white text-uppercase"
                              style={{ fontSize: "30px" }}
                            >
                              {about.name && about.name.charAt(0).toUpperCase()} {about.lastname && about.lastname.charAt(0).toUpperCase()}
                            </span>
                          )}
                        </div>



                        <div className="doc_type mt-3 mb-3">{about.name} {about.lastname}</div>

                        <InfoRow label="Email" value={about.email || NOT_AVAILABLE} />
                        <InfoRow label="Phone Number" value={about.phone || NOT_AVAILABLE} />
                        <InfoRow label="Address" value={formatAddress(about)} />

                        {about.bio &&
                          <InfoRow label="Bio" value={about.bio} />
                        }
                        {about.primary_market &&
                          <InfoRow label="Primary Market" value={about.primary_market} />
                        }

                        {isLender &&
                          <>
                            <InfoRow label="Investment Type" value={mapList(about.invest_type, INVEST_TYPE_LABELS)} />
                            <InfoRow label="Property Types Interested In" value={mapList(about.where_invest, PROPERTY_TYPE_LABELS)} />
                          </>
                        }

                        <div className="row mt-4">
                          <DetailTile label="User Type" value={mapValue(about.usertype, USERTYPE_LABELS)} />
                          <IdenfyStatusTile status={about.idenfyStatus || userInfo?.idenfyStatus} />
                          <SuspendedTile isSuspended={about.is_suspended} />
                          <DetailTile label="Signup Method" value={userInfo?.provider || NOT_AVAILABLE} />

                          <DetailTile label="Joined On" value={formatDate(userInfo?.created)} />
                          <DetailTile label="How They Heard About Us" value={formatReferralSource(about) || NOT_AVAILABLE} />
                          {userInfo?.lastlogin &&
                            <DetailTile label="Last Login" value={formatDate(userInfo?.lastlogin)} />
                          }
                          {(about.years_of_experience !== undefined && about.years_of_experience !== null && about.years_of_experience !== '') &&
                            <DetailTile label="Years of Experience" value={about.years_of_experience} />
                          }
                          {(about.average_rating !== undefined && about.average_rating !== null) &&
                            <DetailTile
                              label="Average Rating"
                              value={`${about.average_rating}${about.total_reviews ? ` (${about.total_reviews} review${about.total_reviews === 1 ? '' : 's'})` : ''}`}
                            />
                          }

                          {isLender &&
                            <>
                              <DetailTile label="Total Capital Allocation" value={formatCurrency(about.invest)} />
                              <DetailTile label="Allocation Per Placement" value={formatIndividualInvest(about.individual_invest)} />
                              <DetailTile label="Investment Duration" value={formatInvestTime(about.invest_time)} />
                              <DetailTile label="Preferred Lien Position" value={mapValue(about.position, POSITION_LABELS)} />
                              <DetailTile label="Minimum Borrower Experience" value={mapValue(about.desired_experience, EXPERIENCE_LABELS)} />
                              <NewsletterTile about={about} />
                            </>
                          }

                          {socialLinks.map((link) => (
                            <SocialLinkTile key={link.key} label={link.label} url={link.url} />
                          ))}
                        </div>


                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </main>
        </div>
      </div>
    </>
  );
};

export default ViewUser;
